import React from 'react';
import styled from '@emotion/styled';
import { theme, SectionWrapper as SectionWrapperStyles } from '@styles';
import { ClubJourneySectionData } from './types';
import { OUTER_SPACE, MOUNTAIN_MEADOW, BLACK_HAZE } from '@constants';

const { breakpoints } = theme;

const ClubJourneySection: React.FC<ClubJourneySectionData> = (props) => {
  const { journey_title, journey_pillars, journey_mockups } = props;

  if (!journey_title || !journey_pillars || journey_pillars.length === 0) {
    console.error('Club journey section: Missing required Prismic data', {
      has_title: !!journey_title,
      has_pillars: !!journey_pillars,
      pillars_count: journey_pillars?.length || 0
    });
    return null;
  }

  return (
    <SectionWrapper>
      <ContentWrapper>
        <SectionTitle>{journey_title}</SectionTitle>

        <PillarsGrid>
          {journey_pillars.map((pillar, index) => (
            <PillarCard key={index}>
              <PillarIcon>
                {pillar.pillar_icon?.url && (
                  <img
                    src={pillar.pillar_icon.url}
                    alt={pillar.pillar_icon.alt || pillar.pillar_title || 'Journey pillar icon'}
                  />
                )}
              </PillarIcon>
              <PillarContent>
                <PillarTitle>{pillar.pillar_title}</PillarTitle>
                <PillarDescription>{pillar.pillar_description}</PillarDescription>
              </PillarContent>
            </PillarCard>
          ))}
        </PillarsGrid>

        {journey_mockups && journey_mockups.length > 0 && (
          <MockupsWrapper>
            <MockupsGrid>
              {journey_mockups.map((mockup, index) => (
                <MockupContainer key={index}>
                  <MockupImage>
                    {mockup.mockup_image?.url && (
                      <img
                        src={mockup.mockup_image.url}
                        alt={mockup.mockup_image.alt || mockup.mockup_label || 'Journey mockup'}
                      />
                    )}
                  </MockupImage>
                  <MockupLabel>{mockup.mockup_label}</MockupLabel>
                </MockupContainer>
              ))}
            </MockupsGrid>
          </MockupsWrapper>
        )}
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default ClubJourneySection;

const SectionWrapper = styled(SectionWrapperStyles)`
  padding: 100px 24px;
  background: ${BLACK_HAZE};
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    padding: 140px 24px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  color: ${OUTER_SPACE};
  text-align: center;
  margin-bottom: 60px;
  font-family: 'Poppins', sans-serif;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 42px;
    line-height: 50px;
    margin-bottom: 80px;
  }
`;

const PillarsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-bottom: 80px;
  
  @media screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;
  }
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    margin-bottom: 120px;
  }
`;

const PillarCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0px 8px 30px rgba(0, 0, 0, 0.12);
  }
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    padding: 40px 32px;
  }
`;

const PillarIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 24px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${MOUNTAIN_MEADOW}20, ${MOUNTAIN_MEADOW}10);
  border-radius: 16px;
  
  img {
    width: 32px;
    height: 32px;
  }
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    width: 72px;
    height: 72px;
    margin-bottom: 32px;
    
    img {
      width: 36px;
      height: 36px;
    }
  }
`;

const PillarContent = styled.div``;

const PillarTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${OUTER_SPACE};
  margin-bottom: 12px;
  font-family: 'Poppins', sans-serif;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 22px;
    line-height: 30px;
    margin-bottom: 16px;
  }
`;

const PillarDescription = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: ${OUTER_SPACE};
  opacity: 0.8;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 17px;
    line-height: 26px;
  }
`;

const MockupsWrapper = styled.div`
  position: relative;
`;

const MockupsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: end;
  
  @media screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
`;

const MockupContainer = styled.div`
  text-align: center;
`;

const MockupImage = styled.div`
  margin-bottom: 20px;
  
  img {
    width: 100%;
    max-width: 250px;
    height: auto;
    filter: drop-shadow(0px 15px 35px rgba(0, 0, 0, 0.15));
  }
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    img {
      max-width: 280px;
    }
  }
`;

const MockupLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${OUTER_SPACE};
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    font-size: 15px;
  }
`; 